import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Calendar } from 'lucide-react';
import { FaEnvelope, FaHeart, FaEye, FaUsers, FaCog } from 'react-icons/fa';
import { MdOutlineMenuBook } from 'react-icons/md';
import api from '../services/api';
import '../styles/Profile.css';
import getProfileImageUrl from '../utils/getProfileImageUrl';  
import getBookCoverUrl from '../utils/getBookCoverUrl';  

const Profile = () => {
  const { userId: profileUserId } = useParams();
  const navigate = useNavigate();
  const currentUserId = localStorage.getItem('userId');
  const token = localStorage.getItem('token');
  const [user, setUser] = useState(null);
  const [isFollowing, setIsFollowing] = useState(false);
  const [books, setBooks] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchProfileData = async () => {
      if (!token) {
        navigate('/login');
        return;
      }
      try {
        if (!profileUserId) {
          setError("Kullanıcı bulunamadı.");
          setIsLoading(false);
          return;
        }
        const response = await api.get(`/profile/${profileUserId}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        const { user: userData, books: userBooks, isFollowing: followStatus } = response.data;
        const profilePhoto = getProfileImageUrl(userData.profile_image_url);
         
        setUser({
          ...userData,
          handle: `@${userData.username}`,
          joinDate: new Date(userData.join_date).toLocaleDateString('tr-TR', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          }),
          profilePhoto
        });
        setBooks(userBooks.map(book => ({
          id: book.book_id,
          title: book.title,
          cover: getBookCoverUrl(book.cover_image_url),
          reads: book.total_views,
          comments: book.total_comments,
          likes: book.total_likes,
          publishedChapters: book.published_chapters_count,
          author: userData.username
        })));
        setIsFollowing(followStatus);
        setIsLoading(false);
      } catch (error) {
        console.error('Profil bilgileri alınırken hata:', error);
        setError('Profil bilgileri yüklenirken bir hata oluştu. Lütfen tekrar deneyin.');
        setIsLoading(false);
        if (error.response?.status === 401) {
          localStorage.removeItem('token');
          localStorage.removeItem('userId');
          navigate('/login');
        }
      }
    };
    fetchProfileData();
    return () => {
      setUser(null);
      setBooks([]);
      setIsFollowing(false);
      setIsLoading(true);
    };
  }, [profileUserId, navigate, token]);

  const handleFollowClick = async () => {
    if (!token) {
      navigate('/login');
      return;
    }
    try {
      if (isFollowing) {
        await api.post(`/profile/unfollow/${profileUserId}`, {}, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
      } else {
        await api.post(`/profile/follow/${profileUserId}`, {}, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
      }
      setIsFollowing(!isFollowing);
      setUser(prevUser => ({
        ...prevUser,
        followers_count: isFollowing ? prevUser.followers_count - 1 : prevUser.followers_count + 1
      }));
    } catch (error) {
      console.error('Takip durumu güncellenirken hata:', error);
    }
  };

  const handleMessageClick = () => {
    navigate(`/inbox?recipient_username=${user.username}`);
  };

  const handleCommunityClick = () => {
    navigate(`/community/${profileUserId}`);
  };

  const handleSettingsClick = () => {
    navigate('/settings');
  };

  if (isLoading) {
    return (
      <div className="profile-loading-container">
        <div className="profile-loading-spinner"></div>
      </div>
    );
  }

  if (error) return <div className="profile-error-message">{error}</div>;
  if (!user) return <div className="profile-error-message">Kullanıcı bulunamadı</div>;

  const isOwnProfile = profileUserId === currentUserId;

  return (
    <div className="profile-container">
      <div className="profile-cover"></div>
      <div className="profile-info-section">
        <div className="profile-header">
          <div className="profile-avatar-wrapper">
            {user.profilePhoto ? (
              <img
                src={getProfileImageUrl(user.profilePhoto)}
                alt={user.username}
                className="profile-avatar"
              />
            ) : (
              <div className="profile-avatar-placeholder">
                {user.username[0].toUpperCase()}
              </div>
            )}
          </div>
          <div className="profile-meta">
            <h1>{user.full_name}</h1>
            <p className="profile-user-handle">{user.handle}</p>
          </div>
          <div className="profile-follow-button-container">
            <div className="profile-icon-wrapper">
              <FaEnvelope className="profile-icon" onClick={handleMessageClick} />
              <FaUsers className="profile-icon" onClick={handleCommunityClick} />
              <FaCog className="profile-icon" onClick={handleSettingsClick} />
            </div>
            {!isOwnProfile && (
              <button className="profile-follow-button" onClick={handleFollowClick}>
                {isFollowing ? 'Takip Ediliyor' : 'Takip Et'}
              </button>
            )}
          </div>
        </div>
      </div>
      <div className="profile-stats">
        <span>{books.length} <span className="profile-stat-label">Kitaplar</span></span>
        <span>{user.followers_count} <span className="profile-stat-label">Takipçiler</span></span>
        <span>{user.following_count} <span className="profile-stat-label">Takip</span></span>
      </div>
      <div className="profile-about">
        <h3>Hakkında</h3>
        <p>{user.bio || 'Henüz bir biyografi eklenmemiş.'}</p>
        <div className="profile-join-date">
          <Calendar className="profile-icon" /> Üye Oldu: {user.joinDate}
        </div>
      </div>
      <div className="profile-books">
        {books.length > 0 && (
          <>
            <h2>{user.full_name} tarafından yazılmış hikayeler</h2>
            <div className="profile-books-grid">
              {books.map(book => (
                <Link to={`/book/${book.id}`} key={book.id} className="profile-book-item">
                  <div className="profile-book-cover-container">
                    <img src={getBookCoverUrl(book.cover)} alt={book.title} className="profile-book-cover" />
                  </div>
                  <div className="profile-book-info">
                    <h3>{book.title}</h3>
                    <p className="profile-book-author">{book.author} tarafından</p>
                    <div className="profile-book-stats">
                      <span className="profile-stat-item"><FaEye /> {book.reads}</span>
                      <span className="profile-stat-item"><FaHeart /> {book.likes}</span>
                      <span className="profile-stat-item"><MdOutlineMenuBook /> {book.publishedChapters}</span> 
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Profile;
